import axios from 'axios';
import BaseApi from './base-api';
import numeral from 'numeral';

class ServiceProvidersApi extends BaseApi {
  getServiceProviders(type, byName, page, loadingDisabled = true) {
    let queryString = `type=${type}&page=${page || 1}`;

    if (byName) {
      queryString += `&by_name=${byName}`;
    }

    return axios
      .get(`${this.baseUrl}/service_providers?${queryString}`, {
        loadingDisabled: loadingDisabled,
      })
      .then((response) => {
        return {
          ok: true,
          total: numeral(response.headers['total']).value(),
          pageSize: numeral(response.headers['per-page']).value(),
          serviceProviders: response.data,
        };
      })
      .catch((error) => {
        return {
          ok: false,
          errors: error.response.data.errors,
        };
      });
  }

  showServiceProviderById(type, id, loadingDisabled = true) {
    let queryString = `type=${type}`;

    return axios
      .get(`${this.baseUrl}/service_providers/${id}?${queryString}`, {
        loadingDisabled: loadingDisabled,
      })
      .then((response) => {
        return {
          ok: true,
          serviceProvider: response.data,
        };
      })
      .catch((error) => {
        return {
          ok: false,
          errors: error.response.data.errors,
        };
      });
  }

  getServiceProviderUsers(type, serviceProviderId) {
    return axios
      .get(
        `${this.baseUrl}/service_providers/${serviceProviderId}/users?type=${type}`
      )
      .then((response) => {
        return {
          ok: true,
          users: response.data,
        };
      })
      .catch((error) => {
        return {
          ok: false,
          errors: error.response.data.errors,
        };
      });
  }

  createServiceProvider(type, values) {
    const data = this.serviceProviderParams(values);
    const headers = { headers: { 'Content-Type': 'multipart/form-data' } };
    return axios
      .post(`${this.baseUrl}/service_providers?type=${type}`, data, headers)
      .then((response) => {
        return {
          ok: true,
          serviceProvider: response.data,
        };
      })
      .catch((error) => {
        return {
          ok: false,
          errors: error.response.data.errors,
        };
      });
  }

  updateServiceProvider(type, id, values) {
    const data = this.serviceProviderParams(values);
    const headers = { headers: { 'Content-Type': 'multipart/form-data' } };
    return axios
      .put(
        `${this.baseUrl}/service_providers/${id}?type=${type}`,
        data,
        headers
      )
      .then((response) => {
        return {
          ok: true,
          serviceProvider: response.data,
        };
      })
      .catch((error) => {
        return {
          ok: false,
          errors: error.response.data.errors,
        };
      });
  }

  editUser(values) {
    const data = this.userParams(values.type, values);

    return axios
      .put(
        `${this.baseUrl}/service_providers/${values.accountableId}/users/${values.id}`,
        data
      )
      .then((response) => {
        return {
          ok: true,
          updatedUser: response.data,
        };
      })
      .catch((error) => {
        return {
          ok: false,
          errors: error.response.data.errors,
        };
      });
  }

  deleteUser(values) {
    return axios
      .delete(
        `${this.baseUrl}/service_providers/${values.accountableId}/users/${values.id}?type=${values.type}`
      )
      .then((response) => {
        return {
          ok: true,
        };
      })
      .catch((error) => {
        return {
          ok: false,
          errors: error.response.data.errors,
        };
      });
  }

  createServiceProviderDirectory(type, values) {
    let data = new FormData();
    data.append('type', type);
    data.append('service_provider[name]', values.name);
    data.append(
      'service_provider[contact_attributes][first_name]',
      values.contact.firstName
    );
    data.append(
      'service_provider[contact_attributes][last_name]',
      values.contact.lastName
    );
    data.append(
      'service_provider[contact_attributes][email]',
      values.contact.email
    );
    data.append(
      'service_provider[contact_attributes][phone_number]',
      values.contact.phoneNumber
    );
    data.append(
      'service_provider[contact_attributes][phone_ext]',
      values.contact.phoneExt
    );
    data.append(
      'service_provider[contact_attributes][title]',
      values.contact.title
    );

    if (values.image && values.image.name && values.image.preview) {
      data.append('service_provider[logo_attributes][image]', values.image);
    }

    const headers = { headers: { 'Content-Type': 'multipart/form-data' } };

    return axios
      .post(`${this.baseUrl}/service_providers`, data, headers)
      .then((response) => {
        return {
          ok: true,
          serviceProvider: response.data,
        };
      })
      .catch((error) => {
        return {
          ok: false,
          errors: error.response.data.errors,
        };
      });
  }

  inviteUser(type, serviceProviderId, values) {
    const data = this.userParams(type, values);

    return axios
      .post(
        `${this.baseUrl}/service_providers/${serviceProviderId}/invite_user`,
        data
      )
      .then((response) => {
        return {
          ok: true,
          user: response.data,
        };
      })
      .catch((error) => {
        return {
          ok: false,
          errors: error.response.data.errors,
        };
      });
  }

  sendInvite(user) {
    const { id, accountable_id, accountable_type } = user;

    return axios
      .put(
        `${this.baseUrl}/service_providers/${accountable_id}/users/${id}/send_invite?type=${accountable_type}`
      )
      .then((response) => {
        return {
          ok: true,
          user: response.data,
        };
      })
      .catch((error) => {
        return {
          ok: false,
          errors: error.response.data.errors,
        };
      });
  }

  activationLink(user) {
    const { id, accountable_id, accountable_type } = user;

    return axios
      .get(
        `${this.baseUrl}/service_providers/${accountable_id}/users/${id}/accept_invitation_url?type=${accountable_type}`
      )
      .then((response) => {
        return {
          ok: true,
          instructions: response.data,
        };
      })
      .catch((error) => {
        return {
          ok: false,
          errors: error.response.data.errors,
        };
      });
  }

  serviceProviderParams(values) {
    const data = new FormData();
    if (values.name !== undefined) {
      data.append('service_provider[name]', values.name);
      if (values.contactId) {
        data.append(
          'service_provider[contact_attributes][id]',
          values.contactId
        );
      }

      data.append(
        'service_provider[contact_attributes][first_name]',
        values.firstName
      );
      data.append(
        'service_provider[contact_attributes][last_name]',
        values.lastName
      );
      data.append('service_provider[contact_attributes][email]', values.email);
      data.append(
        'service_provider[contact_attributes][phone_number]',
        values.phoneNumber
      );

      if (values.locationId) {
        data.append(
          'service_provider[location_attributes][id]',
          values.locationId
        );
      }

      data.append(
        'service_provider[location_attributes][street_address_1]',
        values.streetAddress
      );
      data.append(
        'service_provider[location_attributes][street_address_2]',
        values.unitNumber || ''
      );
      data.append('service_provider[location_attributes][city]', values.city);
      data.append('service_provider[location_attributes][state]', values.state);
      data.append('service_provider[location_attributes][zip]', values.zipCode);
    }

    if (values.logo) {
      if (values.logo.id) {
        data.append('service_provider[logo_attributes][id]', values.logo.id);
      }

      if (values.logo.destroy) {
        data.append('service_provider[logo_attributes][_destroy]', true);
      } else if (values.logo.file) {
        const { file } = values.logo;
        data.append(
          'service_provider[logo_attributes][image]',
          file,
          file.name
        );
      }
    }

    if (values.stamp) {
      const { stamp } = values;
      if (stamp.id) {
        data.append('service_provider[stamp_attributes][id]', stamp.id);
      }

      if (stamp.destroy) {
        data.append('service_provider[stamp_attributes][_destroy]', true);
      } else {
        data.append('service_provider[stamp_attributes][legal]', stamp.legal);
        stamp.stampActions &&
          stamp.stampActions.forEach((stampAction, index) => {
            if (stampAction.id) {
              data.append(
                `service_provider[stamp_attributes][stamp_actions_attributes][${index}][id]`,
                stampAction.id
              );
            }

            if (stampAction.destroy) {
              data.append(
                `service_provider[stamp_attributes][stamp_actions_attributes][${index}][_destroy]`,
                stampAction.destroy
              );
            } else {
              data.append(
                `service_provider[stamp_attributes][stamp_actions_attributes][${index}][action]`,
                stampAction.action
              );
              data.append(
                `service_provider[stamp_attributes][stamp_actions_attributes][${index}][title]`,
                stampAction.title
              );
              data.append(
                `service_provider[stamp_attributes][stamp_actions_attributes][${index}][subtitle]`,
                stampAction.subtitle || ''
              );
            }
          });
      }
    }

    return data;
  }

  userParams(type, values) {
    return {
      type: type,
      user: {
        first_name: values.firstName,
        last_name: values.lastName,
        email: values.email,
        phone_number: values.phoneNumber,
        phone_ext: values.phoneExt,
        title: values.title,
      },
    };
  }
}

export const serviceProvidersApi = new ServiceProvidersApi();



// WEBPACK FOOTER //
// ./src/services/api/service-providers-api.js